import { API_URLS } from '../urls';

/** Une diffusion de chaîne : de son allumage à son arrêt (`endDate` nul = en cours). */
export interface LiveStatRow {
  id       : number;
  name     : string;
  startDate: string;
  endDate  : string | null;
  active   : boolean;
  /** Adresses IP distinctes ayant regardé pendant la diffusion. */
  viewers  : number;
}

/** Une vidéo regardée : première et dernière lecture. */
export interface VideoStatRow {
  id       : number;
  name     : string;
  startDate: string;
  endDate  : string;
  viewers  : number;
}

export interface HistogramBucket {
  start     : string;
  /** Adresses IP distinctes présentes dans la tranche. */
  viewers   : number;
  /** Adresses IP distinctes vues depuis le début jusqu'à la fin de la tranche. */
  cumulative: number;
  /** Villes des spectateurs présents dans cette tranche (absent tant que l'API ne le fournit pas). */
  locations?: ViewerLocation[];
  /** Spectateurs distincts par mode de lecture (absents tant que l'API ne les fournit pas). */
  viewersHls?: number;
  viewersXtream?: number;
  unlocated?: number;
}

/** Une ville où l'on a regardé (les adresses IP, elles, ne sortent pas de l'API). */
export interface ViewerLocation {
  lat    : number;
  lng    : number;
  city   : string;
  country: string;
  /** Spectateurs distincts dans cette ville. */
  viewers: number;
  /** Mode de lecture de ces spectateurs ; une ville regardée des deux façons a deux entrées. */
  mode?: 'HLS' | 'XTREAM_CODE';
}

/** Un spectateur présent dans une tranche : adresse IP, ville et, si la session a un compte, son identité. */
export interface ViewerDetail {
  ip         : string;
  mode       : 'HLS' | 'XTREAM_CODE';
  /** Null tant que l'adresse n'est pas dans le cache de géolocalisation. */
  city       : string | null;
  country    : string | null;
  /** Code ISO 3166-1 alpha-2 en majuscules (pour le drapeau). */
  countryCode: string | null;
  email      : string | null;
  firstname  : string | null;
  lastname   : string | null;
  from       : string;
  to         : string;
}

export interface Histogram {
  bucketSeconds: number;
  buckets      : HistogramBucket[];
  locations    : ViewerLocation[];
  /** Spectateurs dont l'adresse n'est pas (encore) géolocalisée : absents de la carte. */
  unlocated    : number;
  /** Position de l'adresse IP de celui qui interroge (null si inconnue du cache de géolocalisation). */
  viewer?      : { lat: number; lng: number } | null;
}

/** Le fuseau du navigateur : les tranches se calent sur ses heures rondes, pas sur celles de l'UTC. */
const fuseau = (): string => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch { return 'UTC'; }
};

const getJson = async <T,>(url: string): Promise<T> => {
  // `no-store` : des chiffres périmés sont pires que pas de chiffres.
  const res = await fetch(url, { cache: 'no-store' });

  if (!res.ok) throw new Error(`HTTP ${res.status}`);

  return res.json();
};

export const fetchLiveStats = (adminId: number): Promise<LiveStatRow[]> =>
  getJson(`${API_URLS.getStatisticsV2Live}?admin_id=${adminId}`);

/** `step` : pas voulu en secondes ; absent, l'API choisit. Elle le relève si la période donnerait trop de barres. */
export const fetchLiveHistogram = (adminId: number, id: number, step?: number): Promise<Histogram> =>
  getJson(`${API_URLS.getStatisticsV2LiveHistogram.replace('{id}', String(id))}?admin_id=${adminId}&tz=${encodeURIComponent(fuseau())}${step ? `&step=${step}` : ''}`);

export const fetchVideoStats = (adminId: number): Promise<VideoStatRow[]> =>
  getJson(`${API_URLS.getStatisticsV2Videos}?admin_id=${adminId}`);

export const fetchVideoHistogram = (adminId: number, id: number, step?: number): Promise<Histogram> =>
  getJson(`${API_URLS.getStatisticsV2VideoHistogram.replace('{id}', String(id))}?admin_id=${adminId}&tz=${encodeURIComponent(fuseau())}${step ? `&step=${step}` : ''}`);

/** Qui regardait entre `start` et `end` (dates ISO) : adresses IP et identités, donc réservé à l'écran de détail. */
export const fetchViewersDetail = async (adminId: number, onglet: 'live' | 'video', id: number, start: string, end: string): Promise<ViewerDetail[]> => {
  const base = (onglet === 'live' ? API_URLS.getStatisticsV2LiveViewers : API_URLS.getStatisticsV2VideoViewers).replace('{id}', String(id));
  const res  = await getJson<{ viewers: ViewerDetail[] }>(`${base}?admin_id=${adminId}&start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`);

  return res.viewers;
};
